import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Ingredient,
  IngredientType,
  IngredientDetails,
  IngredientSearchPayload,
  IngredientNutritionalInfo,
  NewIngredientPayload,
  IngredientAspResponse,
  IngredientAspHistoryRequest,
  NutritionInfoHistory,
} from '../shared/model/ingredient-interface';
import { Family } from '../shared/model/family-interface';
import { CONSTANTS_DETAILS } from '../shared';
import { environment } from '../../environments/environment';
import { SnackbarService } from './snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  lastValueFrom,
  Observable,
  of,
} from 'rxjs';
import {
  CellValueChangedEvent,
  IServerSideGetRowsParams,
} from 'ag-grid-enterprise';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class LibraryService {
  //Fix Me
  private baseUrl = environment.APP_BASE_URL;
  private _snackbarService = inject(SnackbarService);
  private translateService = inject(TranslateService);
  private http = inject(HttpClient);
  public lastUpdatedRecord = signal<CellValueChangedEvent | null>(null);
  public serverSidePayLoad = signal<IngredientSearchPayload>({});
  public serverSideUpFrontSearch = signal<IngredientSearchPayload>({});
  public isOpenDialog = signal<boolean>(false);
  public $aspUpdateRequest = new BehaviorSubject<IngredientDetails[]>([]);

  /**
   * Storing or creating a new ingredient.
   *
   * @param {NewIngredientPayload} payload
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public createIngredient(payload: NewIngredientPayload): Promise<{
    ingredients: Ingredient;
    message: string;
    isSuccess: boolean;
    ingredientTypeName: string;
  }> {
    return lastValueFrom(
      this.http.post<{
        ingredients: Ingredient;
        message: string;
        isSuccess: boolean;
        ingredientTypeName: string;
      }>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.CREATE_CUSTOM_INGREDIENT_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('ingredientCreateError') + err.message,
        type: 'error',
      });
      return {
        ingredients: {} as Ingredient,
        message: '',
        isSuccess: false,
        ingredientTypeName: '',
      };
    });
  }

  /**
   * Retrieves family master data.
   *
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public getAllFamily(): Promise<{ families: Family[] }> {
    return lastValueFrom(
      this.http.get<{ families: Family[] }>(
        this.baseUrl + CONSTANTS_DETAILS.FAMILY_BASE_URL,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('familyFetchError') + err.message,
        type: 'error',
      });
      return { families: [] };
    });
  }

  /**
   * Retrieves Ingredient Types master data.
   *
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public getAllIngredientTypes(): Promise<{
    ingredientTypes: IngredientType[];
  }> {
    return lastValueFrom(
      this.http.get<{ ingredientTypes: IngredientType[] }>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.INGREDIENT_TYPE_URL,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('ingredientTypesFetchError') +
          err.message,
        type: 'error',
      });
      return { ingredientTypes: [] };
    });
  }

  /**
   * Retrieves Ingredient Nutritional Information for a ingredient.
   * @param {string} ingredientId
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public getIngredientNutritionalInfo(ingredientId: string): Promise<{
    ingredientNutritions: IngredientNutritionalInfo[];
  }> {
    return lastValueFrom(
      this.http.get<{ ingredientNutritions: IngredientNutritionalInfo[] }>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.INGREDIENT_NUTRITIONAL_INFO_URL +
          `/${ingredientId}`,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('ingredientNutritionalInfoFetchError') +
          err.message,
        type: 'error',
      });
      return { ingredientNutritions: [] };
    });
  }

  /**
   * Updates Ingredient Nutritional Info for an ingredient.
   *
   * @param {IngredientNutritionalInfo} payload
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public updateIngredientNutritionalInfo(payload: {
    ingredientNutritions: IngredientNutritionalInfo;
  }): Promise<{
    isSuccess: boolean;
    message: string;
    ingredientNutritions: IngredientNutritionalInfo;
  }> {
    return lastValueFrom(
      this.http.put<{
        isSuccess: boolean;
        message: string;
        ingredientNutritions: IngredientNutritionalInfo;
      }>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.UPDATE_INGREDIENT_NUTRITIONAL_INFO_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('ingredientFamilyUpdateError') +
          err.message,
        type: 'error',
      });
      return {
        message: '',
        isSuccess: false,
        ingredientNutritions: {} as IngredientNutritionalInfo,
      };
    });
  }

  /**
   * Fetches ASP value history for an ingredient.
   *
   * @param ingredientId
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public getIngredientAspHistory(
    ingredientId: string,
  ): Promise<IngredientAspResponse> {
    return lastValueFrom(
      this.http.get<IngredientAspResponse>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.INGREDIENT_ASP_CURRENT_PRICE +
          `/${ingredientId}`,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('ingredientASPHistoryFetchError') +
          err.message,
        type: 'error',
      });
      return { priceHistory: [], aspDetails: [] };
    });
  }

  /**
   * Updates Ingredient-Family Mapping for an ingredient.
   *
   * @param {IngredientDetails} payload
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public updateIngredientMaster(payload: IngredientDetails): Promise<{
    isSuccess: boolean;
    message: string;
    ingredients: IngredientDetails;
  }> {
    return lastValueFrom(
      this.http.put<{
        isSuccess: boolean;
        message: string;
        ingredients: IngredientDetails;
      }>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.UPDATE_INGREDIENT_FAMILY_MAPPING_URL,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('ingredientFamilyUpdateError') +
          err.message,
        type: 'error',
      });
      return {
        message: '',
        isSuccess: false,
        ingredients: {} as IngredientDetails,
      };
    });
  }

  /**
   * Updates Ingredient ASP Value for an ingredient.
   *
   * @param {IngredientDetails} payload
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public updateIngredientASPMaster(
    payload: IngredientDetails,
    aspUrl?: string,
    methodTypes?: string,
  ): Promise<{ isSuccess: boolean; message: string }> {
    const methodType =
      methodTypes === 'post'
        ? this.http.post<{ isSuccess: boolean; message: string }>(
            this.baseUrl + CONSTANTS_DETAILS.INGREDIENT_BASE_URL + aspUrl,
            payload,
          )
        : this.http.put<{ isSuccess: boolean; message: string }>(
            this.baseUrl + CONSTANTS_DETAILS.INGREDIENT_BASE_URL + aspUrl,
            payload,
          );
    return lastValueFrom(methodType).catch((err) => {
      this._snackbarService.open({
        message:
          this.translateService.instant('ingredientASPUpdateError') +
          err.message,
        type: 'error',
      });
      return { message: '', isSuccess: false };
    });
  }

  /**
   * Fetches Ingredient Family Mapping master data for server side pagination.
   *
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */
  public getIngredientServerSideRecords(
    searchMapping: IngredientSearchPayload | IngredientAspHistoryRequest,
    reqUrl?: string,
  ): Observable<{
    ingredientDetails: IngredientDetails[];
    aspDetails?: IngredientDetails[];
    totalCount: number;
    nutritionalHistories?: IngredientDetails[];
  }> {
    const url = reqUrl
      ? reqUrl
      : CONSTANTS_DETAILS.INGREDIENT_FAMILY_MAPPING_URL;
    return this.http
      .post<{
        ingredientDetails: IngredientDetails[];
        totalCount: number;
      }>(
        this.baseUrl + CONSTANTS_DETAILS.INGREDIENT_BASE_URL + url,
        searchMapping,
      )
      .pipe(
        catchError((err) => {
          this._snackbarService.open({
            message:
              this.translateService.instant('ingredientFetchError') +
              err.message,
            type: 'error',
          });
          return of({ ingredientDetails: [], aspDetails: [], totalCount: 0 });
        }),
      );
  }

  /**
   * Creates server side pagination for ag-grid.
   *
   * @param {IngredientSearchPayload} payloadData
   * @param {number} pageSize
   * @param {number} pageNumber
   * @return {*}
   * @memberof LibraryService
   */
  public createServerSidePagination(
    reqUrl?: string,
    ingredientId?: number,
    datePipe?: DatePipe | undefined,
  ) {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        params.api.setGridOption(
          'cacheBlockSize',
          params.api.paginationGetPageSize(),
        );
        params.api.setGridOption(
          'rowBuffer',
          params.api.paginationGetPageSize(),
        );
        this.serverSidePayLoad.set({
          ...this.serverSideUpFrontSearch(),
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: params.api.paginationGetPageSize(),
        });
        if (ingredientId) {
          this.serverSidePayLoad.update((current) => ({
            ...current,
            ingredientId: ingredientId,
          }));
        }
        if (Object.keys(params.api.getFilterModel()).length) {
          Object.keys(params.api.getFilterModel()).map((key) => {
            this.serverSidePayLoad.update((searchMapping) => {
              let filterModelValue = params.api.getFilterModel()?.[key]?.filter;
              filterModelValue =
                typeof filterModelValue === 'string' &&
                filterModelValue.toLowerCase() === 'y' &&
                key === 'isEnabled'
                  ? true
                  : typeof filterModelValue === 'string' &&
                      filterModelValue.toLowerCase() === 'n' &&
                      key === 'isEnabled'
                    ? false
                    : filterModelValue;
              filterModelValue =
                typeof filterModelValue === 'number'
                  ? filterModelValue.toString()
                  : filterModelValue;
              const keyValue =
                params.api.getFilterModel()?.[key]?.filterType == 'date'
                  ? this.getZuluTimeZoneDate(
                      params.api.getFilterModel()?.[key]?.dateFrom as string,
                    )
                  : filterModelValue;
              return {
                ...searchMapping,
                [key]: keyValue,
              };
            });
          });
        } else {
          this.serverSidePayLoad.update((current) => ({
            ...current,
            pageNumber: current.pageNumber as number,
            pageSize: current.pageSize as number,
          }));
        }
        if (params.api.getColumnState().length) {
          params.api
            .getColumnState()
            .filter((col) => col.sort)
            .map((col) => {
              this.serverSidePayLoad.update((searchMapping) => {
                return {
                  ...searchMapping,
                  sortColumn: col.colId,
                  sortDirection:
                    typeof col.sort === 'string'
                      ? col.sort.toUpperCase()
                      : col.sort,
                };
              });
            });
        }
        this.getIngredientServerSideRecords(this.serverSidePayLoad(), reqUrl)
          .pipe(debounceTime(800))
          .subscribe((response) => {
            let rowRecords: IngredientDetails[] = [];
            if (
              (response &&
                response.ingredientDetails &&
                response.ingredientDetails.length) ||
              (response && response.aspDetails && response.aspDetails.length) ||
              (response.nutritionalHistories &&
                response.nutritionalHistories &&
                response.nutritionalHistories.length)
            ) {
              try {
                let idSequence = 0;
                if (
                  response.ingredientDetails &&
                  response.ingredientDetails.length
                ) {
                  response.ingredientDetails.forEach(
                    (element: IngredientDetails) => {
                      element.isEditable = false;
                      element.id = idSequence++;
                      element.exIngredientTypeIdentifier =
                        element.ingredientTypeIdentifier;
                    },
                  );
                  rowRecords = response.ingredientDetails;
                }
                if (response.aspDetails && response.aspDetails.length) {
                  response.aspDetails.forEach((element: IngredientDetails) => {
                    element.id = idSequence++;
                    element.aspApplicableTo = datePipe?.transform(
                      element.aspApplicableTo,
                      CONSTANTS_DETAILS.DATE_TIME_FORMAT,
                    );
                    element.aspApplicableFrom = datePipe?.transform(
                      element.aspApplicableFrom,
                      CONSTANTS_DETAILS.DATE_TIME_FORMAT,
                    );
                  });
                  rowRecords = response.aspDetails;
                }
                if (
                  response.nutritionalHistories &&
                  response.nutritionalHistories.length
                ) {
                  response.nutritionalHistories.forEach(
                    (element: IngredientDetails) => {
                      element.id = idSequence++;
                      element.modifiedOn = datePipe?.transform(
                        element.modifiedOn,
                        CONSTANTS_DETAILS.DATE_TIME_FORMAT,
                      );
                    },
                  );
                  rowRecords = response.nutritionalHistories;
                }
                params.api.hideOverlay();
                params.api.setGridOption('suppressPaginationPanel', false);
                params.success({
                  rowData: rowRecords,
                  rowCount: response.totalCount,
                });
                if (this.isOpenDialog()) {
                  const filteredData = rowRecords.filter(
                    (i) =>
                      i.ingredientIdentifier ===
                      this.serverSideUpFrontSearch().ingredientIdentifier,
                  );
                  this.$aspUpdateRequest.next(filteredData);
                }
              } catch (error) {
                params.api.setGridOption('suppressPaginationPanel', true);
                params.api.showNoRowsOverlay();
                params.fail();
                throw new Error(error as string);
              }
            } else {
              params.success({ rowData: [], rowCount: 0 });
              params.api.setGridOption('suppressPaginationPanel', true);
              params.api.showNoRowsOverlay();
            }
          });
      },
    };
  }

  /**
   * Retrieves Nutritional history data.
   *
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */

  public getNutritionInfoHistory(
    payload: NutritionInfoHistory,
  ): Observable<{ nutritionalHistories: IngredientNutritionalInfo[] }> {
    return this.http
      .post<{
        nutritionalHistories: IngredientNutritionalInfo[];
      }>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.INGREDIENT_NUTRITIONAL_INFO_HISTORY_URL,
        payload,
      )
      .pipe(
        catchError((err) => {
          this._snackbarService.open({
            message: err.message,
            type: 'error',
          });
          return of({ nutritionalHistories: [] });
        }),
      );
  }

  /**
   * Send ASP request to system admins.
   * @param {IngredientDetails} payload
   * @return {*}  {Observable<any>}
   * @memberof LibraryService
   */

  public async sendIngredientASPRequest(
    payload: IngredientDetails,
  ): Promise<{ message: string; isSuccess: boolean }> {
    return lastValueFrom(
      this.http.post<{
        message: string;
        isSuccess: boolean;
      }>(
        this.baseUrl +
          CONSTANTS_DETAILS.INGREDIENT_BASE_URL +
          CONSTANTS_DETAILS.SEND_UPDATE_ASP_REQUEST,
        payload,
      ),
    ).catch((err) => {
      this._snackbarService.open({
        message: err.message,
        type: 'error',
      });
      return { message: '', isSuccess: false };
    });
  }

  /**
   * Converts a local date string to a Zulu time zone date string.
   *
   * @private
   * @param {string} dateString
   * @return {*}  {string}
   * @memberof LibraryService
   */
  private getZuluTimeZoneDate(dateString: string): string {
    const localeDate = new Date(dateString);
    const zuluDate = new Date(
      localeDate.getTime() - localeDate.getTimezoneOffset() * 60000,
    ).toISOString();
    return zuluDate;
  }
}
